import { catalog } from '../core/catalog.js';
import { t } from '../core/i18n.js';
import { notify } from '../core/notify.js';
import { escapeHtml } from '../core/utils.js';
import { movieCard, seriesCard, empty, enhance } from '../ui/render.js';

/* Página de actor: películas y series del catálogo en cuyo reparto aparece. Los repartos se
   conocen a medida que se abren fichas (caché de detalles); el botón "Buscar en todo el catálogo"
   consulta la ficha de cada título que falte (varias a la vez, se puede detener) y va
   completando la lista. */

const PARALLEL = 3;

function decodeName(raw) {
    try {
        return decodeURIComponent(raw);
    } catch (e) {
        return raw;
    }
}

export default {
    path: '/actor/:name/',
    nav: 'movies',

    render(route) {
        const name = decodeName(route.params.name || '');
        return `<div class="page" data-name="actor">
            <div class="page-content">
                <div class="page-head">
                    <button class="btn btn-icon" data-back aria-label="${t('common.back')}"><span class="mi">arrow_back</span></button>
                    <img class="actor-photo" data-person-photo alt="" hidden/>
                    <h1 class="page-title">${escapeHtml(name)}</h1>
                    <button class="btn" data-scan><span class="mi">manage_search</span>${t('actor.scan')}</button>
                </div>
                <p class="actor-hint" data-hint>${t('actor.hint')}</p>
                <div class="actor-progress" data-progress hidden><div class="spinner"></div><span data-progress-text></span><button class="btn btn-sm btn-ghost" data-stop>${t('actor.stop')}</button></div>
                <div class="page-sub" data-count></div>
                <div data-results><div class="spinner"></div></div>
            </div>
        </div>`;
    },

    mount(page, route) {
        const root = page.el;
        const name = decodeName(route.params.name || '');
        const els = {
            results: root.querySelector('[data-results]'),
            count: root.querySelector('[data-count]'),
            progress: root.querySelector('[data-progress]'),
            progressText: root.querySelector('[data-progress-text]'),
            scan: root.querySelector('[data-scan]'),
            hint: root.querySelector('[data-hint]')
        };
        let stop = false;
        let scanning = false;
        let alive = true;

        const paint = (items) => {
            if (!alive) return;
            const nDir = items.filter((it) => it.roles.indexOf('director') !== -1).length;
            const nAct = items.filter((it) => it.roles.indexOf('actor') !== -1).length;
            els.count.textContent = items.length
                ? [t('actor.results', { count: items.length })].concat(nDir && nAct ? [t('actor.asDirector', { n: nDir }), t('actor.asActor', { n: nAct })] : nDir ? [t('actor.asDirector', { n: nDir })] : []).join(' · ')
                : '';
            const movies = items.filter((it) => it.kind === 'vod');
            const series = items.filter((it) => it.kind === 'series');
            const section = (title, html) => `<section class="section"><div class="section-head"><h2 class="section-title">${title}</h2></div><div class="grid">${html}</div></section>`;
            els.results.innerHTML = items.length
                ? (movies.length ? section(t('search.movies') + ' · ' + movies.length, movies.map((it) => movieCard(it.item)).join('')) : '')
                    + (series.length ? section(t('search.series') + ' · ' + series.length, series.map((it) => seriesCard(it.item)).join('')) : '')
                : empty({ icon: 'person_search', title: t('actor.emptyTitle'), text: t('actor.emptyText') });
            enhance(els.results);
        };

        // Primero lo ya conocido de las fichas abiertas (instantáneo); en cuanto llega la filmografía de TMDB se junta con eso.
        let remote = null;
        const merge = (local, extra) => {
            const byKey = new Map();
            (extra || []).concat(local).forEach((it) => {
                const key = it.kind + ':' + it.id;
                const prev = byKey.get(key);
                if (!prev) byKey.set(key, Object.assign({}, it, { roles: (it.roles || ['actor']).slice() }));
                else (it.roles || []).forEach((r) => { if (prev.roles.indexOf(r) === -1) prev.roles.push(r); });
            });
            return Array.from(byKey.values()).sort((a, b) => b.year - a.year || String(a.item.name).localeCompare(String(b.item.name)));
        };
        const refresh = async () => {
            if (!catalog.loaded) return;
            paint(merge(await catalog.actorFilmography(name), remote && remote.items));
        };
        let remoteLoading = false;
        const loadRemote = async () => {
            if (!catalog.loaded || remote || remoteLoading || !catalog.personSource) return;
            remoteLoading = true;
            els.hint.textContent = t('actor.searching');
            remote = await catalog.personCredits(name);
            remoteLoading = false;
            if (!alive) return;
            if (remote.person && remote.person.photo) {
                const img = root.querySelector('[data-person-photo]');
                if (img) { img.src = remote.person.photo; img.hidden = false; }
            }
            els.hint.textContent = remote.person ? t('actor.fromTmdb') : t('actor.hint');
            els.hint.hidden = !remote.person && !els.hint.textContent;
            // Con la filmografía de TMDB cruzada con el catálogo, rastrear ficha a ficha ya no hace falta salvo que no haya resultados.
            if (remote.person && remote.items.length) els.scan.hidden = true;
            await refresh();
        };

        const scan = async () => {
            if (scanning || !catalog.loaded) return;
            scanning = true;
            stop = false;
            els.scan.disabled = true;
            els.progress.hidden = false;
            els.progressText.textContent = t('actor.scanning', { done: 0, total: catalog.visible('vod').length + catalog.visible('series').length });
            let last = 0;
            await catalog.scanDetails({
                parallel: PARALLEL,
                shouldStop: () => stop,
                onProgress: (done, total) => {
                    els.progressText.textContent = t('actor.scanning', { done, total });
                    if (done - last >= 40) { last = done; refresh(); }
                }
            });
            scanning = false;
            if (!alive) return;
            els.progress.hidden = true;
            els.scan.disabled = false;
            if (!stop) {
                els.hint.hidden = true;
                notify.toast(t('actor.scanDone'), { icon: 'check' });
            }
            refresh();
        };

        root.addEventListener('click', (e) => {
            if (e.target.closest('[data-scan]')) { e.preventDefault(); scan(); return; }
            if (e.target.closest('[data-stop]')) { e.preventDefault(); stop = true; }
        });

        refresh().then(loadRemote);
        const off = catalog.on('change', () => { if (!scanning) refresh().then(loadRemote); });
        return () => {
            alive = false;
            stop = true;
            off();
        };
    }
};
